import React, { useMemo, useState, useEffect } from 'react';
import { Box, Paper, Stack, useTheme } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Breadcrumb from '../common/Breadcrumb';
import PageLayout from '../common/PageLayout';
import IssuesTable from './components/IssuesTable';
import SmartCheckYearFilter from './filters/SmartCheckYearFilter';
import SmartCheckAssetFilter from './filters/SmartCheckAssetFilter';
import SmartCheckShowIgnoredFilter from './filters/SmartCheckShowIgnoredFilter';
import FilterChip from '../transactions/filters/FilterChip';
import ClearAllButton from '../transactions/filters/ClearAllButton';

interface AssetYearFilters {
    selectedAssets: string[];
    selectedYears: string[];
}

interface IssueTransaction {
    id: string | number;
    issueType?: 'missingAssetValue' | 'costBasis';
    currency?: string;
    taxYear?: number;
    gain?: number;
    acquired_at?: string;
    sold_at?: string | string[];
}

interface SmartCheckDetailsPageProps {
    allIssuesData: IssueTransaction[];
    userCurrency: string;
    isLoading: boolean;
    isRecalculating: boolean;
    processingSavedTransactions: Set<string | number>;
    ignoringIssues: Set<string | number>;
    allIgnoredIssues: Record<string, any>;
    ignoredIssuesCount: number;
    highIgnoredCount: number;
    lowIgnoredCount: number;
    pricingIgnoredCount: number;
    onIgnore: (transactionId: string | number, issueType: string, currency: string, taxYear?: number) => Promise<void>;
    isIgnored: (transactionId: string | number, taxYear?: number) => boolean;
}

const SmartCheckDetailsPage: React.FC<SmartCheckDetailsPageProps> = ({
    allIssuesData,
    userCurrency,
    isLoading,
    isRecalculating,
    processingSavedTransactions,
    ignoringIssues,
    allIgnoredIssues,
    ignoredIssuesCount,
    highIgnoredCount,
    lowIgnoredCount,
    pricingIgnoredCount,
    onIgnore,
    isIgnored,
}) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Get filter from URL
    const urlFilter = searchParams.get('filter') || 'all';
    const urlYear = searchParams.get('year');
    const urlAsset = searchParams.get('asset');
    const showIgnored = searchParams.get('showIgnored') === 'true';

    // Sorting and pagination state
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<string>('sold_at');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    // Asset/Year filters
    const [assetYearFilters, setAssetYearFilters] = useState<AssetYearFilters>({
        selectedAssets: urlAsset ? urlAsset.split(',') : [],
        selectedYears: urlYear ? urlYear.split(',') : []
    });

    // Update filters when URL params change
    useEffect(() => {
        setAssetYearFilters({
            selectedAssets: urlAsset ? urlAsset.split(',') : [],
            selectedYears: urlYear ? urlYear.split(',') : []
        });
    }, [urlAsset, urlYear]);

    // Filter issues based on URL filter parameter
    const filteredIssues = useMemo(() => {
        let filtered = [...allIssuesData];

        // Apply top-level filter (all, high, low, pricing)
        switch (urlFilter) {
            case 'high':
                filtered = filtered.filter(
                    (issue) => issue.issueType === 'costBasis' && Math.abs(issue.gain || 0) > 10
                );
                break;
            case 'low':
                filtered = filtered.filter(
                    (issue) => issue.issueType === 'costBasis' && Math.abs(issue.gain || 0) <= 10
                );
                break;
            case 'pricing':
                filtered = filtered.filter((issue) => issue.issueType === 'missingAssetValue');
                break;
            case 'all':
            default:
                // No additional filter
                break;
        }

        // Exclude ignored issues unless showIgnored is enabled
        if (!showIgnored) {
            filtered = filtered.filter((issue) => {
                return !(allIgnoredIssues && issue.id?.toString() in allIgnoredIssues);
            });
        }

        // Apply asset filter
        if (assetYearFilters.selectedAssets.length > 0) {
            filtered = filtered.filter((issue) =>
                assetYearFilters.selectedAssets.includes(issue.currency || '')
            );
        }

        // Apply year filter
        if (assetYearFilters.selectedYears.length > 0) {
            filtered = filtered.filter((issue) =>
                assetYearFilters.selectedYears.includes(issue.taxYear?.toString() || '')
            );
        }

        return filtered;
    }, [allIssuesData, urlFilter, assetYearFilters, allIgnoredIssues, showIgnored]);

    // Get base issues for filter options (before asset/year filtering)
    const baseFilteredIssues = useMemo(() => {
        let filtered = [...allIssuesData];

        switch (urlFilter) {
            case 'high':
                filtered = filtered.filter(
                    (issue) => issue.issueType === 'costBasis' && Math.abs(issue.gain || 0) > 10
                );
                break;
            case 'low':
                filtered = filtered.filter(
                    (issue) => issue.issueType === 'costBasis' && Math.abs(issue.gain || 0) <= 10
                );
                break;
            case 'pricing':
                filtered = filtered.filter((issue) => issue.issueType === 'missingAssetValue');
                break;
            case 'all':
            default:
                break;
        }

        if (!showIgnored) {
            filtered = filtered.filter((issue) => {
                return !(allIgnoredIssues && issue.id?.toString() in allIgnoredIssues);
            });
        }

        return filtered;
    }, [allIssuesData, urlFilter, allIgnoredIssues, showIgnored]);

    // Calculate available years (filtered by selected assets if any)
    const availableYears = useMemo(() => {
        let issues = baseFilteredIssues;

        // Filter by selected assets first
        if (assetYearFilters.selectedAssets.length > 0) {
            issues = issues.filter(issue =>
                assetYearFilters.selectedAssets.includes(issue.currency || '')
            );
        }

        const yearMap = new Map<number, number>();
        issues.forEach(issue => {
            if (issue.taxYear) {
                yearMap.set(issue.taxYear, (yearMap.get(issue.taxYear) || 0) + 1);
            }
        });
        return Array.from(yearMap.entries())
            .map(([year, count]) => ({ year, count }))
            .sort((a, b) => b.year - a.year);
    }, [baseFilteredIssues, assetYearFilters.selectedAssets]);

    const availableAssets = useMemo(() => {
        let issues = baseFilteredIssues;

        // Filter by selected years first
        if (assetYearFilters.selectedYears.length > 0) {
            issues = issues.filter(issue =>
                assetYearFilters.selectedYears.includes(issue.taxYear?.toString() || '')
            );
        }

        const assetMap = new Map<string, number>();
        issues.forEach(issue => {
            if (issue.currency) {
                assetMap.set(issue.currency, (assetMap.get(issue.currency) || 0) + 1);
            }
        });
        return Array.from(assetMap.entries())
            .map(([asset, count]) => ({ asset, count }))
            .sort((a, b) => b.count - a.count);
    }, [baseFilteredIssues, assetYearFilters.selectedYears]);

    // Filter change handlers
    const handleYearsChange = (years: number[]) => {
        const newParams = new URLSearchParams(searchParams);
        if (years.length > 0) {
            newParams.set('year', years.join(','));
        } else {
            newParams.delete('year');
        }
        setSearchParams(newParams);
    };

    const handleAssetsChange = (assets: string[]) => {
        const newParams = new URLSearchParams(searchParams);
        if (assets.length > 0) {
            newParams.set('asset', assets.join(','));
        } else {
            newParams.delete('asset');
        }
        setSearchParams(newParams);
    };

    const handleClearFilter = (filterType: 'year' | 'asset', value: string) => {
        if (filterType === 'year') {
            const newYears = assetYearFilters.selectedYears.filter(y => y !== value);
            handleYearsChange(newYears.map(y => parseInt(y)));
        } else {
            const newAssets = assetYearFilters.selectedAssets.filter(a => a !== value);
            handleAssetsChange(newAssets);
        }
    };

    const handleClearAll = () => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('year');
        newParams.delete('asset');
        setSearchParams(newParams);
    };

    const handleShowIgnoredChange = (show: boolean) => {
        const newParams = new URLSearchParams(searchParams);
        if (show) {
            newParams.set('showIgnored', 'true');
        } else {
            newParams.delete('showIgnored');
        }
        setSearchParams(newParams);
    };

    // Build breadcrumb label with filters in brackets
    const getBreadcrumbLabel = () => {
        let baseLabel = '';

        switch (urlFilter) {
            case 'high':
                baseLabel = t('smartCheck.filters.high');
                break;
            case 'low':
                baseLabel = t('smartCheck.filters.low');
                break;
            case 'pricing':
                baseLabel = t('smartCheck.filters.pricing');
                break;
            default:
                baseLabel = t('smartCheck.filters.all');
                break;
        }

        // Add year/asset filters in brackets if present
        const filters: string[] = [];
        if (urlYear) filters.push(urlYear);
        if (urlAsset) filters.push(urlAsset);

        if (filters.length > 0) {
            return `${baseLabel} (${filters.join(', ')})`;
        }

        return baseLabel;
    };

    const breadcrumbItems = [
        { label: t('smartCheck.overview.title'), path: '/smart-check' },
        { label: getBreadcrumbLabel() }
    ];

    const selectedYears = assetYearFilters.selectedYears.map(y => parseInt(y));
    const selectedAssets = assetYearFilters.selectedAssets;
    const hasActiveFilters = selectedYears.length > 0 || selectedAssets.length > 0;

    // Calculate relevant ignored count based on current filter
    const relevantIgnoredCount = useMemo(() => {
        switch (urlFilter) {
            case 'high':
                return highIgnoredCount;
            case 'low':
                return lowIgnoredCount;
            case 'pricing':
                return pricingIgnoredCount;
            case 'all':
            default:
                return ignoredIssuesCount;
        }
    }, [urlFilter, highIgnoredCount, lowIgnoredCount, pricingIgnoredCount, ignoredIssuesCount]);

    return (
        <PageLayout>
            <Breadcrumb items={breadcrumbItems} />

            <Box sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: '100%', md: '1400px', lg: '1600px' },
                mx: 'auto',
            }}>
                {/* Filters */}
                {(availableYears.length > 0 || availableAssets.length > 0 || relevantIgnoredCount > 0) && (
                    <Paper
                        sx={{
                            boxShadow: 'none',
                            borderRadius: 0,
                            bgcolor: 'background.paper',
                            border: 'none',
                            mb: 2
                        }}
                        elevation={0}
                    >
                        <Box sx={{
                            py: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0,
                            width: '100%',
                            mb: 0
                        }}>
                            <Box sx={{ width: '100%' }}>
                                <Stack
                                    direction={{ xs: 'column', md: 'row' }}
                                    spacing={2}
                                    alignItems="flex-start"
                                    sx={{ width: '100%' }}
                                >
                                    {availableYears.length > 0 && (
                                        <SmartCheckYearFilter
                                            years={availableYears}
                                            value={selectedYears}
                                            onChange={handleYearsChange}
                                        />
                                    )}
                                    {availableAssets.length > 0 && (
                                        <SmartCheckAssetFilter
                                            assets={availableAssets}
                                            value={selectedAssets}
                                            onChange={handleAssetsChange}
                                        />
                                    )}
                                    {relevantIgnoredCount > 0 && (
                                        <SmartCheckShowIgnoredFilter
                                            value={showIgnored}
                                            onChange={handleShowIgnoredChange}
                                            ignoredCount={relevantIgnoredCount}
                                        />
                                    )}
                                </Stack>

                                {/* Active Filters */}
                                {hasActiveFilters && (
                                    <Box sx={{ mt: 1 }}>
                                        <Stack direction="row" spacing={1} sx={{
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            gap: 1
                                        }}>
                                            {selectedYears.map((year) => (
                                                <FilterChip
                                                    key={`year-${year}`}
                                                    label={`${year}`}
                                                    onDelete={() => handleClearFilter('year', year.toString())}
                                                />
                                            ))}
                                            {selectedAssets.map((asset) => (
                                                <FilterChip
                                                    key={`asset-${asset}`}
                                                    label={asset}
                                                    onDelete={() => handleClearFilter('asset', asset)}
                                                />
                                            ))}
                                            <ClearAllButton
                                                onClick={handleClearAll}
                                                label={t('transactions.filters.clearAll', 'Clear All')}
                                            />
                                        </Stack>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Paper>
                )}

                {/* Issues Table */}
                <IssuesTable
                    issues={filteredIssues}
                    userCurrency={userCurrency}
                    onIgnore={onIgnore}
                    isIgnored={isIgnored}
                    order={order}
                    orderBy={orderBy}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onOrderChange={setOrder}
                    onOrderByChange={setOrderBy}
                    onPageChange={setPage}
                    onRowsPerPageChange={setRowsPerPage}
                    total={filteredIssues.length}
                    processingSavedTransactions={processingSavedTransactions}
                    isRecalculating={isRecalculating}
                    isIgnoreLoading={ignoringIssues}
                    isLoading={isLoading}
                />
            </Box>
        </PageLayout>
    );
};

export default SmartCheckDetailsPage;
